import { t } from './lingua';

/**
 * I permessi come li legge una persona (blocco 35): il nome, la parte del
 * pannello dove stanno, cosa aprono e, se sono pericolosi, perché. La pagina
 * «Accessi» li mostra al posto delle chiavi, e un pulsante disabilitato dice
 * quale manca.
 *
 * Le frasi sono chiavi del dizionario: si mostrano con `t()`. Un test
 * dell'hub (internal/authz) fallisce se un permesso configurabile non ha qui
 * la sua voce.
 */
export interface VocePermesso {
  titolo: string;
  area: string;
  apre: string;
  /** Perché concederlo sotto l'admin, o a un cliente, chiede una conferma. */
  pericoloso?: string;
}

export const AREE = [
  'Organizzazione e persone',
  'Server e IP',
  'Istanze',
  'Codice e moduli',
  'Backup e continuità',
  'Domini e copie di prova',
  'Impostazioni, assistenti e traslochi',
] as const;

const [ORG, SERVER, ISTANZE, CODICE, BACKUP, DOMINI, IMPOSTAZIONI] = AREE;

export const PERMESSI: Record<string, VocePermesso> = {
  'org.read': { titolo: 'Vedere l’organizzazione', area: ORG, apre: 'Le pagine dell’organizzazione e gli assistenti collegati' },
  'org.manage': { titolo: 'Cambiare nome e obiettivi', area: ORG, apre: 'Il nome dell’organizzazione; «Salva» degli obiettivi in Continuità' },
  'member.invite': { titolo: 'Invitare persone', area: ORG, apre: '«Invita» e la revoca degli inviti in Persone' },
  'member.manage': {
    titolo: 'Gestire le persone', area: ORG,
    apre: 'La pagina Persone: ruoli, «Rimuovi», accessi sulle risorse, i token di tutti',
    pericoloso: 'Chi gestisce le persone decide chi entra e con quali diritti.',
  },
  'member.owner.manage': { titolo: 'Nominare e togliere owner', area: ORG, apre: 'Il ruolo owner negli inviti e nei cambi di ruolo', pericoloso: 'Un owner governa tutta l’organizzazione.' },
  'token.create': { titolo: 'Creare token e collegare assistenti', area: ORG, apre: 'Token API; «Crea il collegamento» in Assistenti' },
  'audit.read': { titolo: 'Leggere il registro', area: ORG, apre: 'La pagina Registro' },

  'server.read': { titolo: 'Vedere i server', area: SERVER, apre: 'La pagina Server e la scheda di ogni server' },
  'metrics.read': { titolo: 'Vedere le metriche', area: SERVER, apre: 'I grafici di CPU, memoria e disco' },
  'server.create': {
    titolo: 'Creare e collegare server', area: SERVER,
    apre: '«Collega un server», «Crea il server», «Riprendi» la preparazione',
    pericoloso: 'Un server nuovo si paga.',
  },
  'server.delete': {
    titolo: 'Scollegare server', area: SERVER,
    apre: '«Scollega» nella scheda del server; «Dichiara il disastro» in Continuità',
    pericoloso: 'Cancella la macchina e tutto quello che ci gira.',
  },
  'server.ssh': {
    titolo: 'Dare accesso SSH a tempo', area: SERVER,
    apre: '«Dai l’accesso» e «Revoca» in Accesso SSH',
    pericoloso: 'Dà una shell sul server, e da lì si leggono le istanze vicine.',
  },
  'ip.read': { titolo: 'Vedere gli IP fissi', area: SERVER, apre: 'La pagina IP' },
  'ip.manage': {
    titolo: 'Comprare e spostare IP fissi', area: SERVER,
    apre: '«Compra e assegna», «Assegna», «Libera»',
    pericoloso: 'Un IP si paga, e spostarlo sposta il traffico di un cliente.',
  },

  'instance.read': { titolo: 'Vedere le istanze', area: ISTANZE, apre: 'La pagina Istanze e la scheda di ogni istanza' },
  'logs.read': { titolo: 'Leggere log e attività', area: ISTANZE, apre: 'La pagina Attività e i log delle istanze' },
  'instance.create': { titolo: 'Creare istanze', area: ISTANZE, apre: '«Nuova istanza»' },
  'instance.start': { titolo: 'Avviare un’istanza', area: ISTANZE, apre: '«Avvia» nella scheda dell’istanza; il tool start_instance' },
  'instance.stop': {
    titolo: 'Fermare un’istanza', area: ISTANZE,
    apre: '«Ferma» nella scheda dell’istanza; il tool stop_instance',
    pericoloso: 'Spegne la produzione di un cliente.',
  },
  'instance.restart': { titolo: 'Riavviare un’istanza', area: ISTANZE, apre: '«Riavvia» nella scheda dell’istanza; il tool restart_instance' },
  'instance.config.write': { titolo: 'Cambiare worker e memoria', area: ISTANZE, apre: '«Modifica» le risorse e «Rigenera la configurazione»' },
  'instance.login': {
    titolo: 'Entrare in Odoo come un utente', area: ISTANZE,
    apre: '«Entra in Odoo»',
    pericoloso: 'Si entra come chiunque, senza password.',
  },
  'instance.delete': {
    titolo: 'Rimuovere un’istanza', area: ISTANZE,
    apre: '«Rimuovi» nella scheda dell’istanza',
    pericoloso: 'Cancella database e file dell’istanza.',
  },

  'instance.deploy': {
    titolo: 'Collegare repository e librerie', area: CODICE,
    apre: '«Collega», «Scollega», «Cambia ramo», le librerie Python, il Catalogo',
    pericoloso: 'Il codice gira sul server con i diritti di Odoo.',
  },
  'instance.code.pull': {
    titolo: 'Portare i moduli alla punta del ramo', area: CODICE,
    apre: '«Portalo sul server» e «Aggiorna» in Moduli; ogni riconfigurazione di un’istanza con repository',
    pericoloso: 'Porta in produzione il codice nuovo dei rami collegati.',
  },
  'module.install': { titolo: 'Installare moduli', area: CODICE, apre: '«Moduli…» nella scheda dell’istanza; i moduli di un blueprint' },
  'instance.update': {
    titolo: 'Aggiornare Odoo', area: CODICE,
    apre: '«Aggiorna Odoo» e «Attiva Enterprise»',
    pericoloso: 'Ferma la produzione e fa migrare il database.',
  },
  'blueprint.manage': { titolo: 'Gestire i blueprint', area: CODICE, apre: 'La pagina Blueprint: «Nuovo blueprint», modifica, «Elimina»' },

  'backup.read': { titolo: 'Vedere i backup', area: BACKUP, apre: 'Le pagine Backup e Continuità' },
  'backup.create': { titolo: 'Fare un backup', area: BACKUP, apre: '«Backup adesso» e «Prova il ripristino»' },
  'backup.schedule': {
    titolo: 'Pianificare i backup', area: BACKUP,
    apre: '«Attiva» e «Disattiva» la pianificazione',
    pericoloso: 'Disattivarla lascia un’istanza senza backup.',
  },
  'backup.download': {
    titolo: 'Scaricare un backup', area: BACKUP,
    apre: '«Scarica»',
    pericoloso: 'Un backup porta i dati personali dei clienti.',
  },
  'db.restore': { titolo: 'Ripristinare in un’istanza nuova', area: BACKUP, apre: '«Ripristina»' },
  'db.drop': {
    titolo: 'Sostituire i dati della produzione', area: BACKUP,
    apre: '«Sostituisci i dati» in produzione',
    pericoloso: 'I dati di oggi si perdono.',
  },
  'backup.destination.manage': { titolo: 'Scegliere dove vanno i backup', area: BACKUP, apre: '«Nuova destinazione» in Backup' },

  'domain.manage': { titolo: 'Collegare domini', area: DOMINI, apre: '«Collega un dominio tuo», «Scollega», «Riprova»' },
  'staging.create': { titolo: 'Creare copie di prova', area: DOMINI, apre: '«Crea» una copia di prova' },
  'staging.refresh': { titolo: 'Aggiornare una copia dalla produzione', area: DOMINI, apre: '«Aggiorna dalla produzione»' },

  'credential.manage': {
    titolo: 'Collegare chiavi cloud e DNS', area: IMPOSTAZIONI,
    apre: 'Progetto OVH e Cloudflare in Impostazioni',
    pericoloso: 'Le chiavi creano macchine che si pagano.',
  },
  'enterprise.manage': { titolo: 'Gestire Enterprise e catalogo', area: IMPOSTAZIONI, apre: 'Enterprise e catalogo dei moduli in Impostazioni' },
  'alert.manage': { titolo: 'Gestire i recapiti degli avvisi', area: IMPOSTAZIONI, apre: 'Recapiti in Impostazioni' },
  'mcp.manage': { titolo: 'Governare gli assistenti', area: IMPOSTAZIONI, apre: 'Gli interruttori in Assistenti' },
  'migration.manage': {
    titolo: 'Traslocare da CloudPepper', area: IMPOSTAZIONI,
    apre: 'La pagina Traslochi',
    pericoloso: 'Collegare una sorgente dà root su una macchina, e un trasloco spegne la produzione.',
  },
};

/** Il nome di una colonna della matrice; una che il pannello non conosce resta com'è. */
export function nomeColonna(c: string): string {
  const nomi: Record<string, string> = {
    owner: t('Proprietario'),
    admin: t('Amministratore'),
    member: t('Membro'),
    viewer: t('Sola lettura'),
    'g.admin': t('Cliente · admin'),
    'g.operator': t('Cliente · operatore'),
    'g.viewer': t('Cliente · lettura'),
  };
  return nomi[c] ?? c;
}

/** Il nome di un permesso, o la sua chiave se il pannello non lo conosce. */
export function titoloDi(permesso: string): string {
  return PERMESSI[permesso]?.titolo ?? permesso;
}
